<template>
  <div class="zw zw-kamstrup-alarm-settings">
    <div class="ui basic padded segment">
      <div class="ui header">
        <i class="icon bell"></i>
        {{ $t('pages.kamstrup_alarm_settings.title') }}
        <div class="sub header">{{ $t('pages.kamstrup_alarm_settings.description') }}</div>
      </div>

      <div class="ui segment zw-alarm-help">
        <h4 class="ui header">{{ $t('pages.kamstrup_alarm_settings.how_it_works.title') }}</h4>
        <p>{{ $t('pages.kamstrup_alarm_settings.how_it_works.intro') }}</p>
        <div class="ui relaxed list">
          <div class="item">
            <i class="chart line icon"></i>
            <div class="content">
              <div class="header">{{ $t('pages.kamstrup_alarm_settings.how_it_works.percentage_title') }}</div>
              <div class="description">{{ $t('pages.kamstrup_alarm_settings.how_it_works.percentage_text') }}</div>
            </div>
          </div>
          <div class="item">
            <i class="tint icon"></i>
            <div class="content">
              <div class="header">{{ $t('pages.kamstrup_alarm_settings.how_it_works.minimum_title') }}</div>
              <div class="description">{{ $t('pages.kamstrup_alarm_settings.how_it_works.minimum_text') }}</div>
            </div>
          </div>
          <div class="item">
            <i class="bell icon"></i>
            <div class="content">
              <div class="header">{{ $t('pages.kamstrup_alarm_settings.how_it_works.maximum_title') }}</div>
              <div class="description">{{ $t('pages.kamstrup_alarm_settings.how_it_works.maximum_text') }}</div>
            </div>
          </div>
        </div>
      </div>

      <semantic-loading :active="loading" :text="$t('globals.loaders.loading')"></semantic-loading>

      <div class="ui info message" v-if="!loading && defaults">
        {{ $t('pages.kamstrup_alarm_settings.default_settings', {
          threshold: defaults.threshold_percentage,
          min: defaults.min_absolute_volume
        }) }}
      </div>

      <div class="ui warning message" v-if="!loading && !defaults">
        {{ $t('pages.kamstrup_alarm_settings.no_merchant_defaults') }}
      </div>

      <div class="ui message" v-if="!loading && !subscriptions.length">
        {{ $t('pages.kamstrup_alarm_settings.no_subscriptions') }}
      </div>

      <div class="ui segments" v-for="subscription in subscriptions" :key="subscription.id">
        <div class="ui segment">
          <div class="ui stackable grid">
            <div class="eight wide column">
              <h4 class="ui header">
                {{ subscription.product_name || $t('globals.container.subscription') }}
                <div class="sub header">{{ kamstrupUserText(subscription.kamstrup_user) }}</div>
              </h4>
            </div>
            <div class="eight wide right aligned column">
              <div class="ui toggle checkbox">
                <input
                  type="checkbox"
                  :checked="subscription.uses_custom_settings"
                  @change="toggleCustom(subscription, $event.target.checked)"
                >
                <label>{{ $t('pages.kamstrup_alarm_settings.use_custom') }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="ui segment">
          <div class="ui form" :class="{ loading: subscription.saving }">
            <div class="three fields">
              <div class="field">
                <label>{{ $t('pages.kamstrup_alarm_settings.threshold_percentage') }}</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  v-model.number="subscription.form.threshold_percentage"
                  :disabled="!subscription.uses_custom_settings"
                >
                <div class="help-text">{{ $t('pages.kamstrup_alarm_settings.threshold_percentage_help') }}</div>
              </div>
              <div class="field">
                <label>{{ $t('pages.kamstrup_alarm_settings.min_absolute_volume') }}</label>
                <input
                  type="number"
                  min="0.05"
                  max="25"
                  step="0.001"
                  v-model.number="subscription.form.min_absolute_volume"
                  :disabled="!subscription.uses_custom_settings"
                >
                <div class="help-text">{{ $t('pages.kamstrup_alarm_settings.min_absolute_volume_help') }}</div>
              </div>
              <div class="field">
                <label>{{ $t('pages.kamstrup_alarm_settings.max_daily_volume') }}</label>
                <input
                  type="number"
                  min="0.05"
                  max="1000"
                  step="0.001"
                  v-model.number="subscription.form.max_daily_volume"
                  :disabled="!subscription.uses_custom_settings"
                  :placeholder="$t('pages.kamstrup_alarm_settings.optional')"
                >
              </div>
            </div>

            <p class="help-text">{{ $t('pages.kamstrup_alarm_settings.max_daily_volume_help') }}</p>

            <button
              type="button"
              class="ui primary button"
              :class="{ disabled: !subscription.uses_custom_settings }"
              @click="save(subscription)"
            >
              {{ $t('globals.buttons.save') }}
            </button>
            <button
              type="button"
              class="ui button"
              :class="{ disabled: !subscription.uses_custom_settings }"
              @click="reset(subscription)"
            >
              {{ $t('pages.kamstrup_alarm_settings.use_default') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KamstrupAlarmSettings',

  data () {
    return {
      loading: false,
      defaults: null,
      subscriptions: []
    }
  },

  mounted () {
    this.fetch()
  },

  methods: {
    async fetch () {
      this.loading = true

      try {
        const response = await this.axios.get('/subscriptions/kamstrup/alarm-settings', {
          params: {
            merchant_id: this.$store.getters.activeMerchant.id
          }
        })

        this.defaults = response.data.defaults
        this.subscriptions = (response.data.data || []).map(subscription => {
          return {
            ...subscription,
            saving: false,
            form: {
              threshold_percentage: subscription.settings.threshold_percentage,
              min_absolute_volume: subscription.settings.min_absolute_volume,
              max_daily_volume: subscription.settings.max_daily_volume
            }
          }
        })
      } finally {
        this.loading = false
      }
    },

    toggleCustom (subscription, enabled) {
      subscription.uses_custom_settings = enabled

      if (enabled) {
        if (!subscription.form.threshold_percentage && this.defaults) {
          subscription.form.threshold_percentage = this.defaults.threshold_percentage
        }

        if (!subscription.form.min_absolute_volume && this.defaults) {
          subscription.form.min_absolute_volume = this.defaults.min_absolute_volume
        }
      } else {
        this.reset(subscription)
      }
    },

    async save (subscription) {
      if (!subscription.uses_custom_settings) {
        return
      }

      if (!subscription.form.threshold_percentage || !subscription.form.min_absolute_volume) {
        this.$events.$emit('alert', {
          header: this.$t('globals.validation.errors.error'),
          message: this.$t('pages.kamstrup_alarm_settings.required_fields'),
          type: 'error',
          align: 'right'
        })
        return
      }

      subscription.saving = true

      try {
        const data = {
          threshold_percentage: subscription.form.threshold_percentage,
          min_absolute_volume: subscription.form.min_absolute_volume,
          max_daily_volume: subscription.form.max_daily_volume || null
        }

        await this.axios.put(`/subscriptions/${subscription.id}/kamstrup/alarm-settings`, data)
        subscription.uses_custom_settings = true

        this.$events.$emit('alert', {
          header: this.$t('globals.validation.success.success'),
          message: this.$t('pages.kamstrup_alarm_settings.saved'),
          type: 'success',
          align: 'right'
        })
      } catch (error) {
        this.$events.$emit('alert', {
          header: this.$t('globals.validation.errors.error'),
          message: error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : this.$t('globals.validation.errors.try_again_later'),
          type: 'error',
          align: 'right'
        })
      } finally {
        subscription.saving = false
      }
    },

    async reset (subscription) {
      subscription.saving = true

      try {
        await this.axios.delete(`/subscriptions/${subscription.id}/kamstrup/alarm-settings`)

        subscription.uses_custom_settings = false
        subscription.form.threshold_percentage = this.defaults ? this.defaults.threshold_percentage : null
        subscription.form.min_absolute_volume = this.defaults ? this.defaults.min_absolute_volume : null
        subscription.form.max_daily_volume = this.defaults ? this.defaults.max_daily_volume : null
      } finally {
        subscription.saving = false
      }
    },

    kamstrupUserText (kamstrupUser) {
      if (!kamstrupUser) {
        return '—'
      }

      return [
        kamstrupUser.username,
        kamstrupUser.address,
        kamstrupUser.city,
        kamstrupUser.user_number,
        kamstrupUser.meter_number
      ].filter(Boolean).join(' - ')
    }
  }
}
</script>

<style lang="scss">
.zw-kamstrup-alarm-settings {
  .help-text {
    color: #777;
  }

  .ui.toggle.checkbox {
    margin-top: 0.5rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/kamstrup/AlarmSettings.vue